/** Pure date-range calendar helpers for the trip composer. All dates are local `YYYY-MM-DD` strings. */

// CLDR supplemental weekData firstDay: MV starts on Friday, and every unlisted region on Monday.
const SUNDAY_REGIONS = 'AG AS BD BR BS BT BW BZ CA CO DM DO ET GT GU HK HN ID IL IN JM JP KE KH KR LA MH MM MO MT MX MZ NI NP PA PE PH PK PR PT PY SA SG SV TH TT TW UM US VE VI WS YE ZA ZW'.split(' ');
const SATURDAY_REGIONS = 'AF BH DJ DZ EG IQ IR JO KW LY OM QA SD SY'.split(' ');

const WEEKDAY_KEYS = ['day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat'];

const MS_PER_DAY = 86400000;

function intlFirstDay(tag) {
    if (typeof Intl === 'undefined' || typeof Intl.Locale !== 'function') {
        return null;
    }
    try {
        const loc = new Intl.Locale(tag);
        const info = typeof loc.getWeekInfo === 'function' ? loc.getWeekInfo() : loc.weekInfo;
        if (info && Number.isInteger(info.firstDay) && info.firstDay >= 1 && info.firstDay <= 7) {
            // Intl numbers days 1..7 with 7 = Sunday.
            return info.firstDay % 7;
        }
    } catch {
        return null;
    }
    return null;
}

function tableFirstDay(tag) {
    const parts = tag.split('-');
    const language = parts[0].toLowerCase();
    let region = null;
    for (let i = 1; i < parts.length; i++) {
        if (/^([a-z]{2}|\d{3})$/i.test(parts[i])) {
            region = parts[i].toUpperCase();
            break;
        }
    }
    if (region === null && language === 'en') {
        region = 'US';
    }
    if (SUNDAY_REGIONS.includes(region)) {
        return 0;
    }
    if (SATURDAY_REGIONS.includes(region)) {
        return 6;
    }
    if (region === 'MV') {
        return 5;
    }
    return 1;
}

export function weekStartForLocale(locale) {
    const tag = String(locale || '').trim().replace(/_/g, '-');
    if (tag === '') {
        return 1;
    }
    const fromIntl = intlFirstDay(tag);
    return fromIntl === null ? tableFirstDay(tag) : fromIntl;
}

export function weekdayKeys(weekStart) {
    const keys = [];
    for (let i = 0; i < 7; i++) {
        keys.push(WEEKDAY_KEYS[(weekStart + i) % 7]);
    }
    return keys;
}

export function parseIsoDate(str) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(str || ''));
    if (!match) {
        return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const date = new Date(year, month, day);
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
        return null;
    }
    return date;
}

export function toIsoDate(date) {
    const y = String(date.getFullYear()).padStart(4, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function addMonths(year, month, delta) {
    const total = year * 12 + month + delta;
    return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function nightsBetween(start, end) {
    const a = parseIsoDate(start);
    const b = parseIsoDate(end);
    if (!a || !b) {
        return null;
    }
    // UTC day numbers keep the difference whole across DST changes.
    const dayA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate()) / MS_PER_DAY;
    const dayB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) / MS_PER_DAY;
    return dayB - dayA;
}

export function pickRangeDate(start, end, dateStr) {
    if (!start) {
        return { start: dateStr, end: null };
    }
    if (end) {
        return { start: dateStr, end: null };
    }
    // ISO strings of equal length order the same as the dates they name.
    if (dateStr < start) {
        return { start: dateStr, end: null };
    }
    return { start, end: dateStr };
}

export function monthGrid(year, month, opts) {
    const options = opts || {};
    const weekStart = Number.isInteger(options.weekStart) ? options.weekStart : 1;
    const today = options.today || null;
    const start = options.start || null;
    const end = options.end || null;
    const minDate = options.minDate || null;

    const cells = [];
    const firstWeekday = new Date(year, month, 1).getDay();
    const lead = (firstWeekday - weekStart + 7) % 7;
    const monthId = `${year}-${String(month + 1).padStart(2, '0')}`;
    for (let i = 0; i < lead; i++) {
        cells.push({ empty: true, key: `blank-${monthId}-${i}` });
    }

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${monthId}-${String(day).padStart(2, '0')}`;
        const isPast = today !== null && dateStr < today;
        cells.push({
            empty: false,
            key: dateStr,
            day,
            dateStr,
            isToday: dateStr === today,
            isStart: start !== null && dateStr === start,
            isEnd: end !== null && dateStr === end,
            isInRange: start !== null && end !== null && dateStr > start && dateStr < end,
            isPast,
            disabled: minDate !== null && dateStr < minDate,
        });
    }
    return cells;
}
